// Reusing a controller prevents duplicate listeners during hot reload or dynamic mounting.
const controllers = new WeakMap();
let accordionCount = 0;

/**
 * Initializes one accordion block and synchronises its visible and ARIA states.
 *
 * @param {HTMLElement} root
 * @returns {{destroy: () => void}}
 */
export function initAccordion(root) {
    if (!(root instanceof HTMLElement)) {
        throw new TypeError('initAccordion expects an HTMLElement.');
    }

    const existing = controllers.get(root);
    if (existing) return existing;

    accordionCount += 1;
    const instanceId = root.id || `accordion-${accordionCount}`;
    const controlsButton = root.querySelector('.accordion__controls-button');
    const items = Array.from(root.querySelectorAll('.accordion-item'))
        .map((item, index) => {
            const trigger = item.querySelector('.accordion-item__trigger');
            const content = item.querySelector('.accordion-item__content');
            if (!trigger || !content) return null;

            const triggerId = trigger.id || `${instanceId}-trigger-${index + 1}`;
            const contentId = content.id || `${instanceId}-content-${index + 1}`;
            trigger.id = triggerId;
            trigger.setAttribute('aria-controls', contentId);
            content.id = contentId;
            content.setAttribute('aria-labelledby', triggerId);
            setItemState({ trigger, content }, trigger.getAttribute('aria-expanded') === 'true');

            const onClick = () => {
                setItemState(
                    { trigger, content },
                    trigger.getAttribute('aria-expanded') !== 'true',
                );
                updateControls();
            };

            trigger.addEventListener('click', onClick);
            return { trigger, content, onClick };
        })
        .filter(Boolean);

    const onControlsClick = () => {
        const shouldExpand = !items.every(
            ({ trigger }) => trigger.getAttribute('aria-expanded') === 'true',
        );
        items.forEach((item) => setItemState(item, shouldExpand));
        updateControls();
    };

    function updateControls() {
        if (!controlsButton) return;
        const allExpanded =
            items.length > 0 &&
            items.every(({ trigger }) => trigger.getAttribute('aria-expanded') === 'true');
        controlsButton.setAttribute('aria-expanded', String(allExpanded));
        controlsButton.textContent = allExpanded ? 'Collapse all' : 'Expand all';
    }

    if (controlsButton) controlsButton.addEventListener('click', onControlsClick);
    updateControls();

    const controller = {
        destroy() {
            items.forEach(({ trigger, onClick }) => trigger.removeEventListener('click', onClick));
            if (controlsButton) controlsButton.removeEventListener('click', onControlsClick);
            controllers.delete(root);
        },
    };

    controllers.set(root, controller);
    return controller;
}

function setItemState({ trigger, content }, expanded) {
    trigger.setAttribute('aria-expanded', String(expanded));
    content.setAttribute('aria-hidden', String(!expanded));
    content.hidden = !expanded;
}
